// booking/state/booking.reducer.ts
import type {
  BookingError,
  CalendarDate,
  Cart,
  ExtraQuantities,
  LoadingState,
  Session,
  Stage,
  TicketQuantities,
  VoucherCart,
  VoucherQuantities,
} from '../types/booking'

import { CustomerFormFields } from '../stages/CustomerDetails/types'
import { INITIAL_CUSTOMER_FORM_VALUES } from '../stages/CustomerDetails/constants'

import type { BookingConfig, BookingContextKey } from '@booking/types/bookingConfig'
import { resolveBookingConfig } from '@booking/lib/bookingConfig'

export interface BookingState {
  stage: Stage | null

  cart: Cart | VoucherCart | null
  quantities: TicketQuantities
  voucherQuantities: VoucherQuantities
  extras: ExtraQuantities
  selectedAddons: string[]
  calendarDate: CalendarDate | null
  session: Session | null
  customerData: CustomerFormFields //CartCustomer
  paymentReady: boolean
  hasCompleted: boolean

  loading: LoadingState | null
  error: BookingError | null

  // ✅ NEW: dynamic config driven by booking context
  contextKey: BookingContextKey
  config: BookingConfig
}

const defaultConfig = resolveBookingConfig('default')

export const initialBookingState: BookingState = {
  stage: null,

  cart: null,
  quantities: [],
  voucherQuantities: [],
  extras: [],
  selectedAddons: [],
  calendarDate: new Date(),
  session: null,
  customerData: INITIAL_CUSTOMER_FORM_VALUES,
  paymentReady: false,
  hasCompleted: false,

  loading: null,
  error: null,

  // ✅ NEW
  contextKey: 'default',
  config: defaultConfig,
}

export type BookingAction =
  | { type: 'SET_STAGE'; stage: Stage }
  | { type: 'SET_CART'; cart: Cart | VoucherCart | null }
  | { type: 'SET_TICKET_QUANTITIES'; quantities: TicketQuantities }
  | { type: 'SET_VOUCHER_QUANTITIES'; voucherQuantities: VoucherQuantities }
  | { type: 'SET_EXTRAS'; extras: ExtraQuantities }
  | { type: 'SET_SELECTED_ADDONS'; selectedAddons: string[] }
  | { type: 'SET_SESSION'; session: Session | null }
  | { type: 'SET_CALENDAR_DATE'; calendarDate: CalendarDate | null }
  | { type: 'SET_CUSTOMER_DATA'; customerData: CustomerFormFields }
  | { type: 'SET_PAYMENT_READY'; paymentReady: boolean }
  | { type: 'SET_HAS_COMPLETED'; hasCompleted: boolean }
  | { type: 'SET_LOADING'; loading: LoadingState | null }
  | { type: 'SET_ERROR'; error: BookingError | null }
  | { type: 'RESET_STATE'; state?: Partial<BookingState> }
  // ✅ NEW
  | { type: 'SET_CONTEXT_KEY'; contextKey: BookingContextKey }
  | { type: 'SET_CONFIG'; config: BookingConfig }

export function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'SET_STAGE':
      return { ...state, stage: action.stage }

    case 'SET_CART':
      return { ...state, cart: action.cart }

    case 'SET_TICKET_QUANTITIES':
      return { ...state, quantities: action.quantities }

    case 'SET_VOUCHER_QUANTITIES':
      return { ...state, voucherQuantities: action.voucherQuantities }

    case 'SET_EXTRAS':
      return { ...state, extras: action.extras }

    case 'SET_SELECTED_ADDONS':
      return { ...state, selectedAddons: action.selectedAddons }

    case 'SET_CALENDAR_DATE':
      return { ...state, calendarDate: action.calendarDate }

    case 'SET_SESSION':
      return { ...state, session: action.session }

    case 'SET_CUSTOMER_DATA':
      return { ...state, customerData: action.customerData }

    case 'SET_PAYMENT_READY':
      return { ...state, paymentReady: action.paymentReady }

    case 'SET_HAS_COMPLETED':
      return { ...state, hasCompleted: action.hasCompleted }

    case 'SET_LOADING':
      return { ...state, loading: action.loading }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_CONTEXT_KEY': {
      const config = resolveBookingConfig(action.contextKey)
      const quantities = config.defaultQuantities ?? []
      return {
        ...state,
        contextKey: action.contextKey,
        config,
        quantities,
        voucherQuantities: [],
        extras: [],
        selectedAddons: [],
      }
    }

    // ✅ NEW: explicit config override (e.g. from API)
    case 'SET_CONFIG':
      return { ...state, contextKey: action.config.context, config: action.config }

    case 'RESET_STATE':
      return { ...initialBookingState, ...(action.state ?? {}) }

    default:
      return state
  }
}
